import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { EXAMPLE_OUTCOMES, PIPELINE, ACTIVITY_EXAMPLES } from "@/lib/demo-data";
import { useSEO } from "@/lib/useSEO";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { OutcomeFlow } from "@/components/OutcomeFlow";
import { OutcomeCard } from "@/components/OutcomeCard";
import { CTASection } from "@/components/CTASection";
import { SectionHeader } from "@/components/SectionHeader";
import { Counter } from "@/components/Counter";
import { Reveal } from "@/components/Reveal";
import { DataPanel, MonoLabel } from "@/components/primitives";
import { PageShell } from "@/components/PageShell";

function Home({ ready = true }: { ready?: boolean }) {
  useSEO({
    title: "Consequence Protocol — The Outcome Layer for Autonomous Economies",
    path: "/",
  });

  return (
    <PageShell testId="page-home">
      <Hero ready={ready} />
      <Marquee />

      {/* Pipeline */}
      <section className="container-t py-24 sm:py-32" data-testid="section-pipeline">
        <SectionHeader
          index="01"
          label="System Pipeline"
          title={
            <>
              Action → <span className="text-cyan">Consequence</span>
            </>
          }
          description="Every action that enters the protocol moves through one pipeline. Each stage is explicit, measurable, and inspectable — from the first action to the reputation it leaves behind."
        />
        <div className="mt-14">
          <OutcomeFlow />
        </div>
      </section>

      {/* Problem */}
      <section className="border-t border-line bg-panel/40 py-24 sm:py-32" data-testid="section-problem">
        <div className="container-t grid items-start gap-14 lg:grid-cols-2">
          <div>
            <SectionHeader
              index="02"
              label="The Problem"
              title={
                <>
                  Activity is <span className="text-error">not impact.</span>
                </>
              }
            />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-dim sm:text-lg">
                Modern systems reward activity. But activity does not necessarily equal value.
              </p>
              <p className="mt-4 text-base leading-relaxed text-dim sm:text-lg">
                None of the numbers on the right automatically prove meaningful impact. Volume is a signal of presence —
                nothing more.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 border-l-2 border-cyan pl-6">
                <MonoLabel className="text-cyan">Proof of Consequence</MonoLabel>
                <p className="mt-4 font-display text-xl font-semibold leading-snug text-ink sm:text-2xl">
                  “The measure is not whether you acted.
                  <br />
                  The measure is what happened because you acted.”
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <DataPanel title="Activity Ledger · Illustrative" meta="DEMO" className="w-full">
              <div className="space-y-5">
                {ACTIVITY_EXAMPLES.map((a) => (
                  <div key={a.label} className="flex items-center justify-between gap-4 border-b border-line/60 pb-4 last:border-0">
                    <span className="font-display text-2xl font-bold text-ink sm:text-3xl" data-testid={`activity-counter-${a.label.replace(/\s+/g, "-")}`}>
                      <Counter to={a.value} />
                    </span>
                    <span className="flex items-center gap-3 text-right">
                      <MonoLabel className="text-[9px]">{a.label}</MonoLabel>
                      <span className="font-mono text-xs text-error">✗ NO IMPACT PROVEN</span>
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-between border-t border-line pt-4">
                <MonoLabel className="text-[9px]">Activity ≠ impact</MonoLabel>
                <MonoLabel className="text-[9px] text-cyan">Illustrative figures</MonoLabel>
              </div>
            </DataPanel>
          </Reveal>
        </div>
      </section>

      {/* Examples */}
      <section className="container-t py-24 sm:py-32" data-testid="section-examples">
        <SectionHeader
          index="03"
          label="Real Examples"
          title={
            <>
              Claims become <span className="text-cyan">consequences</span>
            </>
          }
          description="Actors state a claim. The protocol measures reality against it. Verified results become track records — missed claims are recorded just as honestly."
        />
        <Reveal delay={0.1}>
          <div className="mt-8 inline-flex items-center gap-2 border border-warn/40 bg-warn/10 px-3 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-warn" />
            <MonoLabel className="text-[9px] text-warn">Demonstration examples — illustrative UI, not live protocol data</MonoLabel>
          </div>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {EXAMPLE_OUTCOMES.map((ex, i) => (
            <OutcomeCard key={ex.id} example={ex} index={i} />
          ))}
        </div>
      </section>

      {/* Pipeline strip echo */}
      <section className="border-t border-line bg-panel/40 py-16" data-testid="section-visual-story">
        <div className="container-t">
          <Reveal>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3 sm:justify-between">
              {PIPELINE.filter((_, i) => i % 2 === 0).map((s) => (
                <span key={s.id} className="flex items-center gap-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-dim">
                    <span className="text-cyan">{s.id}</span> · {s.name}
                  </span>
                  <ChevronRight className="h-3 w-3 text-dim/50" aria-hidden="true" />
                </span>
              ))}
              <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-cyan">Economic access</span>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </PageShell>
  );
}

export default Home;
